import {Ok} from '@packages/utilities/fp/result';
import {Component} from '@packages/systems/wfdl';
import {getElementByIdFromComponentMap} from '@packages/utilities/platform-selectors';
import {containsLocalesBindings} from './containsLocalesBindings';
import {isInsideLocalesItem} from './isInsideLocalesItem';

import {LOCALES_MOVE_ERROR, LOCALES_LIST_ITEM_MOVE_ERROR} from '../constants';

type LocalesContextData = {
  componentMap: Component.ComponentMap;
  currentComponent: Component.ComponentType;
};

type ValidateLocalesContextArgs = {
  targetData: LocalesContextData;
  sourceData: LocalesContextData;
  sourceElementId: string;
  targetElementId: string;
};

/**
 * Prevents moving out elements bound
 * to the Locales context to avoid breaking bindings.
 *
 * Keep in mind that a LocalesItem will always be nested
 * inside a LocalesWrapper so we look for localesListItem
 * bindings first.
 *
 * @see public/js/designer-flux/utils/platform/DragDropUtils.ts
 */
export function validateLocalesContext({
  targetData,
  sourceData,
  sourceElementId,
  targetElementId,
}: ValidateLocalesContextArgs) {
  const sourceElement = getElementByIdFromComponentMap(
    sourceData.componentMap,
    sourceElementId
  );

  // If we can't find source element in the source current component,
  // then the source element is a new element, not a moved or pasted
  // existing element or a clone of an existing element.
  //
  // packages/systems/dynamo/gateway/binding-context/validateDynamicContext/index.ts:52
  if (!sourceElement) {
    return Ok(sourceElementId);
  }

  const contains = containsLocalesBindings(sourceElement);

  if (!contains.localesListItem) {
    return Ok(sourceElementId);
  }

  const targetElement = getElementByIdFromComponentMap(
    targetData.componentMap,
    targetElementId
  );

  if (!targetElement) {
    return LOCALES_MOVE_ERROR;
  }

  const root = Component.getRender(targetData.currentComponent);
  const isTargetInsideLocalesItem = isInsideLocalesItem(targetElement, root);

  if (contains.localesListItem && !isTargetInsideLocalesItem) {
    return LOCALES_LIST_ITEM_MOVE_ERROR;
  }

  return Ok(sourceElementId);
}
